import PostalMime from 'postal-mime'

/**
 * Convert HTML to plain text-like content so reply separators can be detected
 * more reliably without accidentally cutting normal email text.
 */
function htmlToComparableText(html: string): string {
    const doc = new DOMParser().parseFromString(
        html || '<div></div>',
        'text/html'
    )

    if (!doc.body) {
        return ''
    }

    for (const br of Array.from(doc.querySelectorAll('br'))) {
        br.replaceWith('\n')
    }

    for (const element of Array.from(
        doc.body.querySelectorAll(
            'div, p, table, tbody, tr, td, section, li, hr'
        )
    )) {
        element.append('\n')
    }

    return (doc.body.textContent || '')
        .replace(/\u00a0/g, ' ')
        .replace(/[ \t]+/g, ' ')
        .replace(/\n[ \t]+/g, '\n')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim()
}

/**
 * Detect whether a block of text is a real reply header.
 * We require a reasonably complete header block, not just a single "Od:" or "From:"
 * occurrence, because normal emails can contain these words in the body.
 */
function isReplyHeaderText(text: string): boolean {
    const normalized = (text || '')
        .replace(/\u00a0/g, ' ')
        .replace(/[ \t]+/g, ' ')
        .trim()

    if (!normalized) {
        return false
    }

    if (
        /-----Original Message-----/i.test(normalized) ||
        /----- Forwarded message -----/i.test(normalized) ||
        /Begin forwarded message:/i.test(normalized)
    ) {
        return true
    }

    if (/(^|\n|\r)\s*On .{3,300} wrote:\s*($|\n|\r)/i.test(normalized)) {
        return true
    }

    if (/(^|\n|\r)\s*Dne .{3,300} napsal\(a\):\s*($|\n|\r)/i.test(normalized)) {
        return true
    }

    if (/(^|\n|\r)\s*Dne .{3,300} napsal:\s*($|\n|\r)/i.test(normalized)) {
        return true
    }

    if (
        /(^|\n|\r)\s*Dňa .{3,300} napísal\(a\):\s*($|\n|\r)/i.test(normalized)
    ) {
        return true
    }

    const hasFrom = /(^|\n|\r)\s*(From|Od):\s+/i.test(normalized)
    const hasSent =
        /(^|\n|\r)\s*(Sent|Odeslano|Odesláno|OdeslĂˇno|Odoslane|Odoslané|OdoslanĂ©):\s+/i.test(
            normalized
        )
    const hasTo = /(^|\n|\r)\s*(To|Komu):\s+/i.test(normalized)
    const hasSubject =
        /(^|\n|\r)\s*(Subject|Predmet|Předmět|PĹ™edmÄ›t):\s+/i.test(normalized)

    return hasFrom && hasSent && (hasTo || hasSubject)
}

/**
 * Remove quoted/replied/forwarded content and keep only the latest visible email.
 */
function extractLatestEmailHtml(html: string): string {
    if (!html) {
        return ''
    }

    const doc = new DOMParser().parseFromString(
        html || '<div></div>',
        'text/html'
    )

    if (!doc.body) {
        return '<p></p>'
    }

    const selectorsToRemove = [
        '#divRplyFwdMsg',
        '#appendonsend',
        '.gmail_quote',
        '.moz-cite-prefix',
        'blockquote',
        '[type="cite"]',
        '.yahoo_quoted',
        '.protonmail_quote',
        '.OutlookMessageHeader',
        '.ms-outlook-mobile-reference-message',
        '#mail-editor-reference-message-container',
    ]

    for (const selector of selectorsToRemove) {
        for (const element of Array.from(doc.querySelectorAll(selector))) {
            element.remove()
        }
    }

    const removeNodeAndFollowingContent = (node: Node, root: Element): void => {
        let current: Node | null = node

        while (current && current !== root) {
            let sibling = current.nextSibling

            while (sibling) {
                const nextSibling = sibling.nextSibling
                sibling.parentNode?.removeChild(sibling)
                sibling = nextSibling
            }

            const parent = current.parentNode
            parent?.removeChild(current)
            current = parent
        }
    }

    const elements: Element[] = []
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_ELEMENT)

    let node = walker.nextNode()
    while (node) {
        elements.push(node as Element)
        node = walker.nextNode()
    }

    for (const element of elements) {
        const comparableText = htmlToComparableText(element.outerHTML || '')

        if (isReplyHeaderText(comparableText)) {
            removeNodeAndFollowingContent(element, doc.body)
            break
        }
    }

    let cleaned = doc.body.innerHTML.trim()

    const hardSeparators = [
        /<div[^>]*id=["']divRplyFwdMsg["'][\s\S]*$/i,
        /<div[^>]*id=["']appendonsend["'][\s\S]*$/i,
        /<div[^>]*class=["'][^"']*gmail_quote[^"']*["'][\s\S]*$/i,
        /<blockquote[\s\S]*$/i,
        /-----Original Message-----[\s\S]*$/i,
        /----- Forwarded message -----[\s\S]*$/i,
        /Begin forwarded message:[\s\S]*$/i,
        /(^|\n|\r)\s*On .{3,300} wrote:[\s\S]*$/i,
        /(^|\n|\r)\s*Dne .{3,300} napsal\(a\):[\s\S]*$/i,
        /(^|\n|\r)\s*Dne .{3,300} napsal:[\s\S]*$/i,
        /(^|\n|\r)\s*Dňa .{3,300} napísal\(a\):[\s\S]*$/i,
        /(^|\n|\r)\s*(From|Od):\s+.{1,300}(\n|\r|\r\n)\s*(Sent|Odeslano|Odesláno|OdeslĂˇno|To|Komu|Subject|Předmět|PĹ™edmÄ›t):[\s\S]*$/i,
    ]

    for (const separator of hardSeparators) {
        cleaned = cleaned.replace(separator, '')
    }

    cleaned = cleaned.trim()

    if (cleaned) {
        return cleaned
    }

    // Fallback bez celého vlákna:
    // když HTML parser všechno odstraní nebo nenajde čistou hranici,
    // vezmeme jen text před první odpovědní hlavičkou.
    let textFallback = htmlToComparableText(html)

    const textSeparators = [
        /-----Original Message-----[\s\S]*$/i,
        /----- Forwarded message -----[\s\S]*$/i,
        /Begin forwarded message:[\s\S]*$/i,
        /(^|\n|\r)\s*On .{3,300} wrote:[\s\S]*$/i,
        /(^|\n|\r)\s*Dne .{3,300} napsal\(a\):[\s\S]*$/i,
        /(^|\n|\r)\s*Dne .{3,300} napsal:[\s\S]*$/i,
        /(^|\n|\r)\s*Dňa .{3,300} napísal\(a\):[\s\S]*$/i,
        /(^|\n|\r)\s*(From|Od):\s+.{1,300}(\n|\r|\r\n)\s*(Sent|Odeslano|Odesláno|OdeslĂˇno|To|Komu|Subject|Předmět|PĹ™edmÄ›t):[\s\S]*$/i,
    ]

    for (const separator of textSeparators) {
        textFallback = textFallback.replace(separator, '')
    }

    textFallback = textFallback.trim()

    if (!textFallback) {
        return '<p><em>Nepodařilo se bezpečně najít poslední zprávu v tomto vlákně.</em></p>'
    }

    // Bezpečnostní limit, aby se omylem neposlalo obří celé vlákno.
    if (textFallback.length > 12000) {
        textFallback = textFallback.slice(0, 12000).trim()
    }

    return `<div>${textFallback
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>')}</div>`
}
function removeElementAndFollowingSiblings(element: Element): void {
    const parent = element.parentElement

    if (!parent) {
        element.remove()
        return
    }

    let current: ChildNode | null = element

    while (current) {
        const next: ChildNode | null = current.nextSibling
        current.parentNode?.removeChild(current)
        current = next
    }

    if (!parent.textContent?.trim() && parent.parentElement) {
        parent.remove()
    }
}

/**
 * Clean Outlook/Gmail generated HTML so it looks better in Odoo chatter.
 * This intentionally keeps basic formatting, links, tables, lists and inline images.
 */
function normalizeEmailBodyForOdoo(html: string): string {
    if (!html) {
        return '<p></p>'
    }

    const doc = new DOMParser().parseFromString(html || '<p></p>', 'text/html')

    if (!doc.body) {
        return html || '<p></p>'
    }

    for (const element of Array.from(
        doc.body.querySelectorAll('script, style, meta, title, link')
    )) {
        element.remove()
    }

    for (const element of Array.from(doc.body.querySelectorAll('*'))) {
        const attributes = Array.from(element.attributes)

        for (const attribute of attributes) {
            const name = attribute.name.toLowerCase()

            if (
                name === 'href' ||
                name === 'src' ||
                name === 'alt' ||
                name === 'title' ||
                name === 'colspan' ||
                name === 'rowspan'
            ) {
                continue
            }

            if (
                name === 'class' ||
                name === 'style' ||
                name === 'id' ||
                name === 'lang' ||
                name === 'dir' ||
                name.startsWith('data-') ||
                name.startsWith('aria-')
            ) {
                element.removeAttribute(attribute.name)
            }
        }
    }

    const cleaned = doc.body ? doc.body.innerHTML.trim() : ''

    return cleaned || html || '<p></p>'
}

/**
 * Represent the current email open in the Outlook application.
 */
export class Email {
    subject: string
    timestamp: number
    messageId: string

    emailFrom: string
    emailTo: string
    emailCC: string
    contacts: EmailContact[]

    constructor() {
        const mailbox = Office?.context?.mailbox
        const mail = mailbox?.item

        if (!mailbox || !mail) {
            this.subject = ''
            this.timestamp = Date.now()
            this.messageId = ''

            this.emailFrom = ''
            this.emailTo = ''
            this.emailCC = ''
            this.contacts = []

            return
        }

        const userEmail = mailbox.userProfile.emailAddress

        const values = [
            [mail.from.displayName, mail.from.emailAddress],
            ...mail.to.map((v) => [v.displayName, v.emailAddress]),
            ...mail.cc.map((v) => [v.displayName, v.emailAddress]),
        ]

        this.subject = mail.subject
        this.timestamp = mail.dateTimeCreated.getTime()
        this.messageId = mail.internetMessageId

        const mailToString = (mail: Office.EmailAddressDetails): string => {
            return mail.displayName != mail.emailAddress
                ? `"${mail.displayName}" <${mail.emailAddress}>`
                : mail.emailAddress
        }

        this.emailFrom = mailToString(mail.from)
        this.emailTo = mail.to.map(mailToString).join(', ')
        this.emailCC = mail.cc.map(mailToString).join(', ')
        this.contacts = values
            .filter((v) => v[1] !== userEmail)
            .map((v) => new EmailContact(v[0], v[1]))
    }

    /**
     * Return the content of the email.
     */
    async getBody(): Promise<string> {
        let body: string = await new Promise((resolve) => {
            Office.context.mailbox.item.body.getAsync(
                Office.CoercionType.Html,
                async (result) => {
                    resolve(result.value)
                }
            )
        })

        body = extractLatestEmailHtml(body)
        body = normalizeEmailBodyForOdoo(body)

        // Add inline images.
        const emailB64: string = await new Promise((resolve) => {
            Office.context.mailbox.item.getAsFileAsync((result) => {
                resolve(result.value)
            })
        })

        const email = await PostalMime.parse(atob(emailB64))

        const toBase64 = (buffer: ArrayBuffer) => {
            let binary = ''
            const bytes = new Uint8Array(buffer)
            for (let i = 0; i < bytes.byteLength; i++) {
                binary += String.fromCharCode(bytes[i])
            }
            return btoa(binary)
        }

        for (const attachment of email.attachments) {
            if (!attachment.contentId || attachment.disposition !== 'inline') {
                continue
            }

            const contentId = attachment.contentId
                .replace('<', '')
                .replace('>', '')

            const content = attachment.content as ArrayBuffer
            const base64Data = `data:${attachment.mimeType};base64,${toBase64(content)}`
            body = body.replace(`cid:${contentId}`, base64Data)
        }

        return body
    }

    /**
     * Return the list of attachments in the email if they do not exceed the limit.
     */
    async getAttachments(): Promise<[string, string][] | null> {
        const officeAttachmentDetails = Office.context.mailbox.item.attachments

        const totalSize = officeAttachmentDetails
            .map((officeAttachment) => officeAttachment.size)
            .reduce((partialSum, a) => partialSum + a, 0)

        const SIZE_THRESHOLD_TOTAL = 40 * 1024 * 1024

        if (totalSize > SIZE_THRESHOLD_TOTAL) {
            return null
        }

        const promises = officeAttachmentDetails
            .filter((attachment) => !attachment.isInline)
            .map((attachment) => this.fetchAttachmentContent(attachment))

        return Promise.all(promises)
    }

    /**
     * Get the content of the corresponding attachment.
     */
    fetchAttachmentContent(
        attachment: Office.AttachmentDetails
    ): Promise<[string, string]> {
        return new Promise((resolve) => {
            Office.context.mailbox.item.getAttachmentContentAsync(
                attachment.id,
                (asyncResult) =>
                    resolve([attachment.name, asyncResult.value.content])
            )
        })
    }
}

export class EmailContact {
    name: string
    email: string

    constructor(name: string, email: string) {
        this.name = name
        this.email = email
    }
}
